// What's new (the page): what it shows is in the URL, so a link opens the same view (in another
// tab, or beside the board in Chrome's split view): `?view=whatsnew&days=3&by=area&project=180&asg=Rohit`.
// The period someone picked last is remembered per person on this device. Pure, apart from the
// localStorage helpers (each wrapped: a private window may refuse them).

import {
  briefBody,
  overdueBody,
  briefCounts,
  countsLine,
  DEFAULT_PERIOD,
  filterBrief,
  isPeriod,
  periodParams,
  rangeLabel,
  readPeriod,
  type Brief,
  type BriefGroupBy,
  type BriefLine,
  type News,
  type Period,
} from "@boem/core/brief";
import { NOBODY } from "./components/AssigneeFilter.tsx";
import { readOrgSetting, writeOrgSetting } from "./org.ts";

export interface NewsView {
  period: Period;
  by: BriefGroupBy;
  /** one project's work (its # number); null: the main board */
  project: number | null;
  /** the assignee filter: names, NOBODY for unassigned */
  asg: string[];
}

export const NEWS_DEFAULT: NewsView = { period: DEFAULT_PERIOD, by: "person", project: null, asg: [] };

/** Our parameters; anything else in the URL (panels, ...) is left as it is. */
const KEYS = ["view", "days", "since", "by", "project", "asg"];

export const isNewsUrl = (search: string) => new URLSearchParams(search).get("view") === "whatsnew";

/** Whether the URL names a period (else the person's remembered one applies). */
export const urlHasPeriod = (search: string) => {
  const q = new URLSearchParams(search);
  return isPeriod(readPeriod((k) => q.get(k)));
};

/** The page's view from a URL; a missing or broken part falls back (the period to `period`). */
export function readNews(search: string, period: Period = DEFAULT_PERIOD): NewsView {
  const q = new URLSearchParams(search);
  const p = readPeriod((k) => q.get(k));
  const by = q.get("by");
  const project = Number(q.get("project"));
  const asg = (q.get("asg") ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return {
    period: isPeriod(p) ? p : period,
    by: by === "area" || by === "project" ? by : "person",
    project: Number.isInteger(project) && project > 0 ? project : null,
    asg: [...new Set(asg)].slice(0, 20),
  };
}

/** The query string with the page's view in it, or (null) with it taken out; "" or "?...". */
export function writeNews(search: string, v: NewsView | null): string {
  const old = new URLSearchParams(search);
  const q = new URLSearchParams();
  if (v) {
    q.set("view", "whatsnew");
    for (const [k, val] of periodParams(v.period)) q.set(k, val);
    if (v.by !== "person") q.set("by", v.by);
    if (v.project != null) q.set("project", String(v.project));
    if (v.asg.length) q.set("asg", v.asg.join(","));
  }
  for (const [k, val] of old) if (!KEYS.includes(k)) q.append(k, val);
  const s = q.toString().replace(/%3A/gi, ":").replace(/%2C/gi, ",");
  return s ? `?${s}` : "";
}

/** GET /api/whatsnew's query for a view (the assignee filter stays on the page). */
export function newsQuery(v: NewsView): string {
  const q = new URLSearchParams(periodParams(v.period));
  q.set("by", v.by);
  if (v.project != null) q.set("project", String(v.project));
  return q.toString();
}

// -- The remembered period ---------------------------------------------------------------

/** per person and per board (org.ts), so What's new on two boards keeps two periods */
const periodKey = (viewerId: string) => `buyoem.whatsnew.${viewerId}`;

/** The period this person picked last on this device (default: since the last working day). */
export function savedPeriod(viewerId: string | null | undefined): Period {
  if (!viewerId) return DEFAULT_PERIOD;
  try {
    const q = new URLSearchParams(readOrgSetting(periodKey(viewerId)) ?? "");
    const p = readPeriod((k) => q.get(k));
    return isPeriod(p) ? p : DEFAULT_PERIOD;
  } catch {
    return DEFAULT_PERIOD;
  }
}

export function savePeriod(viewerId: string | null | undefined, p: Period) {
  if (!viewerId) return;
  writeOrgSetting(periodKey(viewerId), new URLSearchParams(periodParams(p)).toString());
}

// -- Filtering and Copy summary ------------------------------------------------------------

/** The lines the assignee filter keeps (none picked: every line). */
export const assigneeKeeps = (asg: string[]) => {
  const want = new Set(asg);
  // a line with several people (OEM-285) is kept when any of them is picked
  return (l: Pick<BriefLine, "assignee" | "assignees">) => {
    if (!want.size) return true;
    const people = l.assignees ?? (l.assignee ? [l.assignee] : []);
    return people.length ? people.some((p) => want.has(p)) : want.has(NOBODY);
  };
};

/** The brief as the page shows it: the assignee filter applied. */
export const shownBrief = (b: Brief, asg: string[]): Brief => (asg.length ? filterBrief(b, assigneeKeeps(asg)) : b);

/**
 * Copy summary: the current view as plain text for Teams, in the meeting brief's shape (each group,
 * its sections, each item as "01-07 (#185) Title — what happened"). Credentials are redacted
 * (briefBody), and only what the page shows is in it.
 */
export function summaryText(news: News, v: NewsView, opts: { project?: string | null } = {}): string {
  const b = shownBrief(news.brief, v.asg);
  const range = rangeLabel(new Date(news.from), new Date(news.to), news.timeZone);
  const scope = [
    opts.project ? `Project ${opts.project} only` : null,
    v.asg.length ? `Assignee: ${v.asg.map((a) => (a === NOBODY ? "Unassigned" : a)).join(", ")}` : null,
  ].filter(Boolean);
  const L = [`What's new on the Board: ${news.label} · ${range}, by ${v.by}${scope.length ? ` · ${scope.join(" · ")}` : ""}`];
  const overdue = overdueBody(b);
  if (!b.groups.length) {
    L.push("Nothing happened on the board in that time.");
    if (overdue.length) L.push("", ...overdue);
    return L.join("\n");
  }
  const counts = countsLine(briefCounts(b));
  if (counts) L.push(counts);
  if (overdue.length) L.push("", ...overdue);
  L.push("", ...briefBody(b));
  return L.join("\n");
}
